import {
  Building2,
  FileInput,
  FileText,
  FileMinus,
  LayoutDashboard,
  Package,
  ReceiptText,
  ScrollText,
  Settings2,
  Users,
  ClipboardList,
  type LucideIcon,
} from "lucide-react";
import { useMatch } from "react-router-dom";
import { Sidebar as LibSidebar, type SidebarNavGroup } from "@marekl/adminer/shell";
import type { User } from "../../auth/queries";
import { FaktioMark } from "../FaktioBrand";
import { SidebarHeader } from "../SidebarHeader";
import { DOC_TYPE_SLUG } from "../../lib/queries/documents";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  /** Hidden from non-admins; the API refuses them anyway. */
  adminOnly?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
  /** A legacy submenu (Prodej ▾): collapsible, keyed by this id. */
  submenu?: string;
}

/**
 * The legacy menu (legacy/app/src/Admin.tsx): Prodej, Nákup, Produkty,
 * Adresář, Nastavení, then the system menu. Prodej, Nákup, Evidence and
 * Nastavení collapse, each on its own (owner, 2026-10-28 — the legacy
 * accordion closed the others); the group of the current page opens itself
 * and the user's choices are remembered.
 */
function companyGroups(c: string): NavGroup[] {
  const docs = (type: keyof typeof DOC_TYPE_SLUG) => `/c/${c}/${DOC_TYPE_SLUG[type]}`;
  return [
    { label: "", items: [{ to: `/c/${c}/overview`, label: "Přehled", icon: LayoutDashboard, end: true }] },
    {
      label: "Prodej",
      submenu: "sales",
      items: [
        { to: docs("sales_invoice"), label: "Vydané faktury", icon: FileText },
        { to: docs("credit_note"), label: "Dobropisy", icon: FileMinus },
        { to: docs("retail"), label: "Prodejky", icon: ReceiptText },
        { to: docs("sales_order"), label: "Cenové nabídky", icon: ClipboardList },
      ],
    },
    { label: "Nákup", submenu: "purchases", items: [{ to: docs("purchase_invoice"), label: "Přijaté faktury", icon: FileInput }] },
    {
      label: "Evidence",
      submenu: "records",
      items: [
        { to: `/c/${c}/products`, label: "Produkty", icon: Package },
        { to: `/c/${c}/contacts`, label: "Adresář", icon: Users },
      ],
    },
    { label: "Nastavení", submenu: "settings", items: [{ to: `/c/${c}/company`, label: "Firma", icon: Building2 }] },
  ];
}

const SYSTEM: NavGroup = {
  label: "Systémové menu",
  items: [
    { to: "/companies", label: "Společnosti", icon: Building2, adminOnly: true },
    { to: "/audit-log", label: "Auditní log", icon: ScrollText, adminOnly: true },
    { to: "/settings", label: "Uživatelé a účet", icon: Settings2 },
  ],
};

interface SidebarProps {
  user: User;
  onLogout: () => void;
}

export function Sidebar({ user, onLogout }: SidebarProps) {
  const match = useMatch("/c/:companyId/*");
  const companyId = match?.params.companyId;
  const navGroups = [
    ...(companyId ? companyGroups(companyId) : []),
    SYSTEM,
  ];
  const groups: SidebarNavGroup[] = navGroups.map((g) => {
    const items = g.items
      .filter((item) => !item.adminOnly || user.role === "admin")
      .map(({ to, label, icon: Icon, end }) => ({
        to,
        label,
        end,
        icon: <Icon aria-hidden="true" />,
      }));
    return g.submenu ? { label: g.label, items, collapsible: true, id: g.submenu } : { label: g.label, items };
  });

  return (
    <LibSidebar
      groups={groups}
      brand={<SidebarHeader user={user} onLogout={onLogout} />}
      brandMark={<FaktioMark />}
      brandBleed
      groupMode="independent"
      persistGroupsKey="faktio.sidebar.groups"
    />
  );
}
